import React, { useEffect, useState, useCallback } from "react";
import { CheckCircle2, XCircle, ExternalLink, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

const TABS = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

const AdminSubmissions = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("pending");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get(`/artists/submissions?status=${tab}`);
      setSubmissions(r.data || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Admin access only");
    } finally {
      setLoading(false);
    }
  }, [user, tab]);

  useEffect(() => {
    load();
  }, [load]);

  const act = async (id, action) => {
    setActingId(id);
    try {
      await api.post(`/artists/submissions/${id}/${action}`);
      toast.success(`Submission ${action}d`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setActingId(null);
    }
  };

  if (!user) {
    return <div className="py-16 text-center text-white/60">Sign in required.</div>;
  }

  return (
    <div data-testid="admin-submissions-page" className="pb-16">
      <div className="mb-8">
        <p className="text-[11px] uppercase tracking-[0.3em] text-white/60 font-semibold mb-2">Admin</p>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <h1 className="font-display text-4xl font-semibold tracking-tighter">Artist Submissions</h1>
          <button
            onClick={load}
            className="p-2 rounded-full bg-white/5 hover:bg-white/15 transition"
            title="Refresh"
            data-testid="admin-refresh-btn"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      <div className="flex gap-2 mb-6">
        {TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium border transition ${
              tab === t.key
                ? "bg-white text-black border-white"
                : "bg-white/[0.04] text-white/70 border-white/10 hover:bg-white/[0.08]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-white/40">Loading…</p>
      ) : submissions.length === 0 ? (
        <p className="text-white/40">No {tab} submissions.</p>
      ) : (
        <div className="space-y-3">
          {submissions.map((s) => (
            <div
              key={s.submission_id}
              data-testid={`submission-${s.submission_id}`}
              className="p-5 rounded-2xl bg-white/[0.03] border border-white/10"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-display text-xl font-semibold">{s.artist_name}</h3>
                    {s.matched ? (
                      <span className="text-xs bg-[#E2FF31]/15 text-[#E2FF31] px-2 py-0.5 rounded-full font-bold">MATCHED</span>
                    ) : (
                      <span className="text-xs bg-[#FF2E00]/15 text-[#FF2E00] px-2 py-0.5 rounded-full font-bold">UNMATCHED</span>
                    )}
                  </div>
                  {s.matched && (
                    <p className="text-sm text-white/60 mb-2">
                      → <span className="font-medium">{s.itunes_artist_name}</span> ({s.itunes_genre}) · iTunes ID {s.itunes_artist_id}
                    </p>
                  )}
                  {s.apple_music_url && (
                    <a
                      href={s.apple_music_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-xs text-white/50 hover:text-white"
                    >
                      <ExternalLink size={12} /> {s.apple_music_url.slice(0, 60)}…
                    </a>
                  )}
                  {s.bio && <p className="text-sm text-white/75 mt-3 leading-relaxed">{s.bio}</p>}
                  <p className="text-xs text-white/40 mt-3">
                    {s.email} · {new Date(s.created_at).toLocaleString()}
                  </p>
                </div>
                {tab === "pending" && (
                  <div className="flex flex-col gap-2 flex-shrink-0">
                    <button
                      data-testid={`approve-${s.submission_id}`}
                      onClick={() => act(s.submission_id, "approve")}
                      disabled={!s.matched || actingId === s.submission_id}
                      className="bg-[#E2FF31] text-black font-semibold rounded-full px-4 py-1.5 text-sm flex items-center gap-1.5 disabled:opacity-30 hover:opacity-90 transition"
                    >
                      <CheckCircle2 size={14} /> Approve
                    </button>
                    <button
                      data-testid={`reject-${s.submission_id}`}
                      onClick={() => act(s.submission_id, "reject")}
                      disabled={actingId === s.submission_id}
                      className="bg-white/10 hover:bg-[#FF2E00]/20 text-white rounded-full px-4 py-1.5 text-sm flex items-center gap-1.5 disabled:opacity-30 transition"
                    >
                      <XCircle size={14} /> Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSubmissions;
